import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, ArrowRight, Loader2, RefreshCw, Smartphone, Monitor, CheckCircle2, Pencil } from "lucide-react";
import { Logo } from "../components/Logo";
import { proxyPageImages } from "../lib/imageProxy";
import GeneratedPageContent from "../components/GeneratedPageContent";
import RegenerateModal from "../components/RegenerateModal";
import { ConversionPanel, MobileConversionBar, progressOf } from "../components/preview/ConversionPanel";
import { UnlockDialog } from "../components/preview/UnlockDialog";
import { ExitIntentDialog, useExitIntent } from "../components/preview/ExitIntentDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STEPS = [
  "Texte werden erstellt...",
  "Design wird angepasst...",
  "Bilder werden ausgewählt...",
  "SEO wird vorbereitet..."
];

function LoadingOverlay({ status, percent }) {
  const currentIdx = STATUS_STEPS.indexOf(status);
  const dp = Math.round(percent);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="regenerate-loading-overlay"
    >
      <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl max-w-sm w-full text-center animate-in fade-in zoom-in-95 duration-200">
        <div className="flex justify-center mb-6">
          <div className="relative w-24 h-24 sm:w-28 sm:h-28">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="44" fill="none" stroke="#e2e8f0" strokeWidth="6" />
              <circle
                cx="50" cy="50" r="44" fill="none"
                stroke="url(#regenGrad)" strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 44}`}
                strokeDashoffset={`${2 * Math.PI * 44 * (1 - dp / 100)}`}
                className="transition-all duration-300 ease-out"
              />
              <defs>
                <linearGradient id="regenGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#6366f1" />
                  <stop offset="50%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#7c3aed" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl sm:text-3xl font-bold text-slate-900 tabular-nums">{dp}</span>
              <span className="text-[10px] text-slate-400 font-medium -mt-0.5">Prozent</span>
            </div>
          </div>
        </div>

        <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-6">
          Neue Variante wird erstellt
        </h2>

        <div className="space-y-2.5 mb-6 max-w-xs mx-auto text-left">
          {STATUS_STEPS.map((step, index) => {
            const isActive = status === step;
            const isPast = currentIdx > index;
            return (
              <div
                key={step}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-500 ${
                  isActive ? "bg-indigo-50 scale-[1.02]" :
                  isPast ? "opacity-100" : "opacity-40"
                }`}
              >
                {isPast ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                ) : isActive ? (
                  <Loader2 className="w-5 h-5 text-indigo-600 animate-spin flex-shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                )}
                <span className={`text-sm font-medium ${
                  isActive ? "text-indigo-700" :
                  isPast ? "text-slate-600" : "text-slate-400"
                }`}>{step}</span>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-400 mt-3">Bitte warte, das dauert einen Moment...</p>
      </div>
    </div>
  );
}

export default function PreviewPage() {
  const navigate = useNavigate();
  const { pageId } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("desktop");
  
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateStatus, setRegenerateStatus] = useState("");
  const [regeneratePercent, setRegeneratePercent] = useState(0);
  const [unlock, setUnlock] = useState(null);
  const exit = useExitIntent(pageId);

  const isLive = !!page && (page.is_published || page.subscription?.status === "active");
  const percent = progressOf(page);
  const goPublish = () => navigate(`/kasse/${pageId}`);

  const handlePanelAction = (action) => (action === "publish" ? goPublish() : setUnlock({ title: "Ihr Logo nach dem Veröffentlichen", text: "Im Dashboard laden Sie Ihr Logo mit einem Klick hoch – genauso wie eigene Bilder, Texte und Google-Bewertungen." }));
  const handleBack = () => { if (isLive || !exit.trigger()) navigate("/assistent"); };

  useEffect(() => {
    fetchPage();
  }, [pageId]);

  const fetchPage = async () => {
    try {
      const response = await axios.get(`${API}/pages/${pageId}`);
      setPage(proxyPageImages(response.data));
    } catch (error) {
      console.error("Failed to fetch page:", error);
      toast.error("Seite nicht gefunden");
      navigate("/assistent");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async (options) => {
    // Close the modal FIRST, then show loading overlay
    setShowRegenerateModal(false);
    setRegenerating(true);

    const statuses = STATUS_STEPS;
    let statusIndex = 0;
    let percent = 0;
    setRegenerateStatus(statuses[0]);
    setRegeneratePercent(0);

    const statusInterval = setInterval(() => {
      statusIndex = Math.min(statusIndex + 1, statuses.length - 1);
      setRegenerateStatus(statuses[statusIndex]);
    }, 2200);

    const percentInterval = setInterval(() => {
      percent = Math.min(percent + 1, 92);
      setRegeneratePercent(percent);
    }, 180);

    const startTime = Date.now();
    const MIN_LOADING_MS = 5000;

    try {
      const response = await axios.post(`${API}/pages/${pageId}/regenerate-with-feedback`, {
        changeTexts: options.changeTexts,
        changeImages: options.changeImages,
        changeColors: options.changeColors,
        changeStructure: options.changeStructure,
        customFeedback: options.customFeedback
      });

      clearInterval(statusInterval);
      clearInterval(percentInterval);

      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_LOADING_MS) {
        setRegenerateStatus(statuses[statuses.length - 1]);
        let p = percent;
        const finishInterval = setInterval(() => {
          p = Math.min(p + 3, 100);
          setRegeneratePercent(p);
          if (p >= 100) clearInterval(finishInterval);
        }, 30);
        await new Promise(r => setTimeout(r, MIN_LOADING_MS - elapsed));
        clearInterval(finishInterval);
      }

      setRegeneratePercent(100);
      setRegenerateStatus(statuses[statuses.length - 1]);
      await new Promise(r => setTimeout(r, 400));

      setPage(prev => ({
        ...prev,
        content: proxyPageImages({ content: response.data.content }).content,
        style: response.data.style || prev.style
      }));

      toast.success("Neue Variante erfolgreich generiert!");
    } catch (error) {
      clearInterval(statusInterval);
      clearInterval(percentInterval);
      console.error("Regeneration error:", error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 402) {
        toast.error(detail, { action: { label: "Paket wählen", onClick: () => navigate(`/kasse/${pageId}`) }, duration: 8000 });
      } else if (detail && [403, 429, 503].includes(status)) {
        toast.error(detail, { duration: 8000 });
      } else {
        toast.error("Fehler beim Regenerieren. Bitte versuche es erneut.");
      }
    } finally {
      setRegenerating(false);
      setRegenerateStatus("");
      setRegeneratePercent(0);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-100 overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 bg-white border-b border-slate-200 px-3 sm:px-4 md:px-8 py-3 sm:py-4 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 sm:gap-4">
          <button 
            onClick={handleBack}
            data-testid="preview-back-btn"
            className="flex items-center gap-1.5 text-slate-600 hover:text-slate-900 transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Zurück</span>
          </button>
          
          <div className="hidden sm:flex items-center gap-2">
            <Logo size="md" clickable={false} />
          </div>

          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <button
              data-testid="view-desktop-btn"
              onClick={() => setViewMode("desktop")}
              className={`p-1.5 sm:p-2 rounded-md transition-colors ${
                viewMode === "desktop" 
                  ? "bg-white shadow-sm text-indigo-600" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Monitor className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <button
              data-testid="view-mobile-btn"
              onClick={() => setViewMode("mobile")}
              className={`p-1.5 sm:p-2 rounded-md transition-colors ${
                viewMode === "mobile" 
                  ? "bg-white shadow-sm text-indigo-600" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Smartphone className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              data-testid="regenerate-btn"
              variant="outline"
              onClick={() => setShowRegenerateModal(true)}
              disabled={regenerating}
              className="rounded-full h-9 sm:h-10 px-3 sm:px-4"
              size="sm"
            >
              <RefreshCw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Andere Variante</span>
            </Button>
            <Button
              data-testid="publish-btn"
              onClick={goPublish}
              className="rounded-full bg-indigo-600 hover:bg-indigo-700 h-9 sm:h-10 px-3 sm:px-4 text-sm"
              size="sm"
            >
              <span className="hidden sm:inline">Veröffentlichen</span>
              <span className="sm:hidden">Weiter</span>
              <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" />
            </Button>
          </div>
        </div>
      </header>

      {/* Preview Container */}
      <main className="flex-1 overflow-auto p-2 sm:p-4 md:p-8 flex justify-center gap-6 items-start">
        <div className="flex-1 min-w-0 max-w-6xl">
        {!isLive && (
          <div className="mx-auto mb-3 max-w-6xl flex items-center gap-2 rounded-xl bg-indigo-50 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm text-slate-700 ring-1 ring-indigo-100" data-testid="cro-edit-banner">
            <Pencil className="h-4 w-4 flex-shrink-0 text-indigo-600" />
            <span><strong>Alles bleibt änderbar:</strong> Texte, Logo, Bilder, Google-Bewertungen u.&nbsp;v.&nbsp;m. passen Sie nach dem Veröffentlichen selbst im Dashboard an.</span>
          </div>
        )}
        <div 
          className={`mx-auto bg-slate-800 rounded-2xl sm:rounded-3xl p-2 sm:p-3 shadow-2xl transition-all duration-300 ${
            viewMode === "mobile" ? "max-w-[400px]" : "max-w-6xl"
          }`}
        >
          <div className="flex items-center gap-2 mb-2 sm:mb-3 px-2">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500"></div>
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></div>
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
            </div>
            <div className="flex-1 bg-slate-700 rounded-lg px-3 sm:px-4 py-1 sm:py-1.5 text-slate-400 text-xs truncate">
              {page?.company_name?.toLowerCase().replace(/\s+/g, '-')}.ihre-domain.de
            </div>
          </div>
          
          <div 
            className="bg-white rounded-lg sm:rounded-xl overflow-auto generated-page-container"
            style={{ 
              containerType: 'inline-size',
              height: viewMode === "mobile" ? "600px" : "70vh",
              maxHeight: "calc(100vh - 180px)"
            }}
          >
            {page && page.content && (
              <GeneratedPageContent page={page} isPreview={true} />
            )}
          </div>
        </div>
        </div>
        {!isLive && <ConversionPanel page={page} onAction={handlePanelAction} onPublish={goPublish} />}
      </main>
      {!isLive && <MobileConversionBar page={page} onPublish={goPublish} />}

      <UnlockDialog reason={unlock} onPublish={goPublish} onClose={() => setUnlock(null)} />
      <ExitIntentDialog open={exit.open} pageId={pageId} percent={percent} onPublish={goPublish} onClose={() => exit.setOpen(false)} />

      {/* Regenerate Options Modal (Dialog - dismissible) */}
      <RegenerateModal
        isOpen={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        onRegenerate={handleRegenerate}
      />

      {/* Loading Overlay (Fixed overlay - NOT a Dialog, cannot be dismissed) */}
      {regenerating && (
        <LoadingOverlay status={regenerateStatus} percent={regeneratePercent} />
      )}
    </div>
  );
}
